import React, { useState, useEffect } from 'react';
import BudgetCalculator from './BudgetCalculator';
import { loadUsers } from '../utils/storage';

const MeetingForm = ({ client, onSave, onCancel, initialMeetingData = null }) => {
  const [users, setUsers] = useState([]);
  // Función para obtener la fecha actual en zona horaria de México
  const getCurrentDateLocal = () => {
    const now = new Date();
    // Convertir a zona horaria de México (America/Mexico_City)
    const mexicoDate = new Date(now.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
    const year = mexicoDate.getFullYear();
    const month = String(mexicoDate.getMonth() + 1).padStart(2, '0');
    const day = String(mexicoDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Función para formatear fecha desde la BD al formato de input (zona horaria México)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Convertir a zona horaria de México (America/Mexico_City)
    const mexicoDate = new Date(date.toLocaleString("en-US", {timeZone: "America/Mexico_City"}));
    const year = mexicoDate.getFullYear();
    const month = String(mexicoDate.getMonth() + 1).padStart(2, '0');
    const day = String(mexicoDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    date: initialMeetingData?.date ? formatDateForInput(initialMeetingData.date) : getCurrentDateLocal(),
    contactsGenerated: initialMeetingData?.contactsGenerated || 0,
    patients: initialMeetingData?.patients || 0,
    nextMeetingDate: initialMeetingData?.nextMeetingDate ? formatDateForInput(initialMeetingData.nextMeetingDate) : '',
    nextMeetingTime: initialMeetingData?.nextMeetingTime || '10:00',
    clientMeetingEmail: initialMeetingData?.clientMeetingEmail || client.email || '',
    meetingType: initialMeetingData?.meetingType || '',
    observations: initialMeetingData?.observations || '',
    agreements: initialMeetingData?.agreements || '',
    objections: initialMeetingData?.objections || {
      price: 0,
      location: 0,
      noResponse: 0,
      schedule: 0,
      other: ''
    },
    servicesSold: initialMeetingData?.servicesSold || [
      { name: '', quantity: 0, value: 0 }
    ],
    tasks: initialMeetingData?.tasks || [
      { 
        description: '', 
        assignedTo: '', 
        dueDate: '', 
        completed: false, 
        completionComment: '',
        createdBy: 'system',
        id: undefined,
        meetingId: initialMeetingData?.id
      }
    ]
  });

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const loadedUsers = await loadUsers();
        setUsers(loadedUsers || []); // Asegurarnos de que siempre sea un array
      } catch (error) {
        console.error('Error cargando usuarios:', error);
        setUsers([]);
      }
    };
    loadUserData();
  }, []);

  useEffect(() => {
    if (!initialMeetingData && client && client.meetings?.length > 0) {
      const lastMeeting = client.meetings[client.meetings.length - 1];
      if (lastMeeting.nextMeetingDate) {
        setFormData(prev => ({
          ...prev,
          nextMeetingDate: lastMeeting.nextMeetingDate,
          nextMeetingTime: lastMeeting.nextMeetingTime || '10:00'
        }));
      }
    }
    if (!initialMeetingData && client.email) {
      setFormData(prev => ({
        ...prev,
        clientMeetingEmail: client.email
      }));
    }
  }, [client, initialMeetingData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'contactsGenerated' || name === 'patients' 
        ? parseInt(value) || 0 
        : value
    }));
  };

  const handleObjectionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      objections: {
        ...prev.objections,
        [name]: name === 'other' ? value : parseInt(value) || 0
      }
    }));
  };

  const handleServiceChange = (index, e) => {
    const { name, value } = e.target;
    const newServices = [...formData.servicesSold];
    newServices[index] = {
      ...newServices[index],
      [name]: name === 'name' ? value : parseFloat(value) || 0
    };
    setFormData(prev => ({ ...prev, servicesSold: newServices }));
  };

  const addServiceField = () => {
    setFormData(prev => ({
      ...prev,
      servicesSold: [...prev.servicesSold, { name: '', quantity: 0, value: 0 }]
    }));
  };

  const removeServiceField = (index) => {
    const newServices = formData.servicesSold.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, servicesSold: newServices }));
  };

  const handleTaskChange = (index, e) => {
    const { name, value } = e.target;
    const newTasks = [...formData.tasks];
    newTasks[index] = {
      ...newTasks[index],
      [name]: value,
      createdBy: 'system',
      meetingId: initialMeetingData?.id
    };
    setFormData(prev => ({ ...prev, tasks: newTasks }));
  };

  const handleTaskCompletionCommentChange = (index, e) => {
    const { value } = e.target;
    const newTasks = [...formData.tasks];
    newTasks[index] = {
      ...newTasks[index],
      completionComment: value
    };
    setFormData(prev => ({ ...prev, tasks: newTasks }));
  };

  const addTaskField = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, { 
        description: '', 
        assignedTo: '', 
        dueDate: '', 
        completed: false, 
        completionComment: '',
        createdBy: 'system',
        id: undefined,
        meetingId: initialMeetingData?.id
      }]
    }));
  };

  const removeTaskField = (index) => {
    const newTasks = formData.tasks.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, tasks: newTasks }));
  };

  const calculateConversionRate = () => {
    if (formData.contactsGenerated === 0) return '0%';
    const rate = (formData.patients / formData.contactsGenerated) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const generateExecutiveSummary = () => {
    const meetingDate = new Date(formData.date);
    const formattedDate = meetingDate.toLocaleDateString('es-MX', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    const nextMeetingDateTime = formData.nextMeetingDate && formData.nextMeetingTime 
      ? `${new Date(formData.nextMeetingDate).toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} a las ${formData.nextMeetingTime} hrs`
      : '';

    let objectionsSummary = '';
    const totalObjections = formData.objections.price + formData.objections.location + formData.objections.noResponse + formData.objections.schedule;
    if (totalObjections > 0 || formData.objections.other) {
      objectionsSummary += `\n\nOBJECIONES DETECTADAS:\n`;
      if (formData.objections.price > 0) objectionsSummary += `• Precio: ${formData.objections.price} personas\n`;
      if (formData.objections.location > 0) objectionsSummary += `• Ubicación: ${formData.objections.location} personas\n`;
      if (formData.objections.noResponse > 0) objectionsSummary += `• No contestan primer mensaje: ${formData.objections.noResponse} personas\n`;
      if (formData.objections.schedule > 0) objectionsSummary += `• Horarios: ${formData.objections.schedule} personas\n`;
      if (formData.objections.other) objectionsSummary += `• Otras: ${formData.objections.other}\n`;
    }

    let servicesSummary = '';
    const totalServicesValue = formData.servicesSold.reduce((sum, service) => sum + (service.quantity * service.value), 0);
    if (formData.servicesSold.some(s => s.name)) {
      servicesSummary += `\n\nSERVICIOS VENDIDOS:\n`;
      formData.servicesSold.forEach(service => {
        if (service.name) {
          servicesSummary += `• ${service.name}: ${service.quantity} unidades, Valor: $${(service.quantity * service.value).toFixed(2)}\n`;
        }
      });
      servicesSummary += `Total valor de servicios vendidos: $${totalServicesValue.toFixed(2)}\n`;
    }

    let tasksSummary = '';
    if (formData.tasks.some(task => task.description)) {
      tasksSummary += `\n\nTAREAS ASIGNADAS:\n`;
      formData.tasks.forEach(task => {
        if (task.description) {
          tasksSummary += `• ${task.description}`;
          if (task.assignedTo) tasksSummary += ` (Asignado a: ${task.assignedTo})`;
          if (task.dueDate) tasksSummary += ` (Fecha límite: ${task.dueDate})`;
          if (task.completed) tasksSummary += ` (Realizada)`;
          if (task.completionComment) tasksSummary += ` - Comentario: ${task.completionComment}`;
          tasksSummary += `\n`;
        }
      });
    }

    return `MINUTA EJECUTIVA - ${client.name.toUpperCase()}\n` +
           `Fecha: ${formattedDate}\n\n` +
           `___________________________________________\n\n` +
           `CLIENTE: ${client.name}\n` +
           (client.specialty ? `ESPECIALIDAD: ${client.specialty}\n\n` : '\n') +
           (formData.meetingType ? `TIPO DE JUNTA: ${formData.meetingType}\n\n` : '\n') +
           `RESUMEN DE LA REUNIÓN:\n${formData.observations}\n\n` +
           `ACCIONES ACORDADAS:\n${formData.agreements || 'No se registraron acciones específicas'}\n` +
           objectionsSummary +
           servicesSummary +
           tasksSummary +
           (nextMeetingDateTime ? `\nPRÓXIMA REUNIÓN: ${nextMeetingDateTime}\n\n` : '\n') +
           `___________________________________________\n` +
           `Generado el ${new Date().toLocaleDateString('es-MX')}`;
  };

  const generateGoogleCalendarLink = () => {
    if (!formData.nextMeetingDate || !formData.nextMeetingTime) return '';

    const [hours, minutes] = formData.nextMeetingTime.split(':').map(Number);
    const startDate = new Date(formData.nextMeetingDate);
    startDate.setHours(hours, minutes, 0, 0); 
    const endDate = new Date(startDate);
    endDate.setHours(hours + 1, minutes, 0, 0);

    const formatDateTime = (date) => {
      return date.toISOString().replace(/-|:|\.\d{3}/g, '');
    };

    const title = encodeURIComponent(`Doctorweb (${client.name}) - ${formData.meetingType || 'Junta'}`);
    const description = encodeURIComponent(`Esta junta es para revisar los avances de los ajustes realizados en la última junta.\n\nResumen de la junta anterior:\n${formData.observations}\n\nAcciones acordadas:\n${formData.agreements}`);
    const dates = `${formatDateTime(startDate)}/${formatDateTime(endDate)}`;
    const clientEmail = formData.clientMeetingEmail ? `&add=${encodeURIComponent(formData.clientMeetingEmail)}` : '';

    const reminders = `&reminders=true&reminderminutes=1440&reminderminutes=180&reminderminutes=60`;

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${description}${clientEmail}${reminders}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const executiveSummary = generateExecutiveSummary();
    const googleCalendarLink = generateGoogleCalendarLink();

    // Si estamos editando, usar el ID existente, si no, no incluir ID
    const meetingToSave = initialMeetingData ? {
      ...initialMeetingData,
      ...formData,
      id: initialMeetingData.id, // Asegurarnos de mantener el ID original
      executiveSummary,
      clientName: client.name,
      googleCalendarLink
    } : {
      ...formData,
      executiveSummary,
      clientName: client.name,
      googleCalendarLink
    };

    console.log("Meeting to save:", meetingToSave);
    console.log("Initial meeting data:", initialMeetingData);
    
    // Asegurarse de que las tareas tengan los campos necesarios
    if (meetingToSave.tasks) {
      meetingToSave.tasks = meetingToSave.tasks.map(task => ({
        ...task,
        id: task.id || undefined, // Mantener el ID si existe, si no, dejar undefined para que la BD lo genere
        meetingId: initialMeetingData?.id, // Usar el ID de la junta existente si estamos editando
        completionComment: task.completionComment || '',
        createdBy: task.createdBy || 'system'
      }));
    }
    
    onSave(meetingToSave);

    if (googleCalendarLink) {
      alert("Minuta generada. Ahora se abrirá Google Calendar para agendar la próxima reunión.");
      window.open(googleCalendarLink, '_blank');
    } else {
      alert("Minuta generada. No se agendó en Google Calendar porque no se especificó una próxima fecha y/u hora de junta.");
    }
  };

  const meetingTypes = [
    '',
    'Seguimiento de Campañas de Marketing',
    'Capacitación de Role Play',
    'Capacitación de Estrategia de Precio y Oferta',
    'Seguimiento de Role Play'
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-[#0061A8]">
          {initialMeetingData ? `Editar Junta con ${client.name}` : `Nueva Junta con ${client.name}`}
        </h3>
        {client.specialty && <p className="text-sm text-[#63666A]">Especialidad: {client.specialty}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[#63666A]">Fecha de junta</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Contactos generados</label>
          <input
            type="number"
            name="contactsGenerated"
            value={formData.contactsGenerated}
            onChange={handleChange}
            min="0"
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Pacientes concretados</label>
          <input
            type="number"
            name="patients"
            value={formData.patients}
            onChange={handleChange}
            min="0"
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Tasa de conversión</label>
          <div className="mt-1 px-3 py-2 bg-[#D1D3D4] rounded-md text-[#63666A]">
            {calculateConversionRate()}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#63666A]">Próxima junta de seguimiento</label>
          <input
            type="date"
            name="nextMeetingDate"
            value={formData.nextMeetingDate}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-[#63666A]">Hora de la próxima junta</label>
          <input
            type="time"
            name="nextMeetingTime"
            value={formData.nextMeetingTime}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#63666A]">Email del Cliente para Agendamiento</label>
          <input
            type="email"
            name="clientMeetingEmail"
            value={formData.clientMeetingEmail}
            onChange={handleChange}
            placeholder="ejemplo@cliente.com"
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-[#63666A]">Tipo de Junta</label>
          <select
            name="meetingType"
            value={formData.meetingType}
            onChange={handleChange}
            className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          >
            {meetingTypes.map(type => (
              <option key={type} value={type}>{type === '' ? 'Seleccione un tipo de junta' : type}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#63666A]">Resumen de la reunión</label>
        <textarea
          name="observations"
          value={formData.observations}
          onChange={handleChange}
          rows={4}
          placeholder="Describa los puntos clave tratados en la reunión..."
          className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-[#63666A]">Acciones acordadas</label>
        <textarea
          name="agreements"
          value={formData.agreements}
          onChange={handleChange}
          rows={3}
          placeholder="Enumere las acciones acordadas durante la reunión..."
          className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
        />
      </div>

      {/* Sección de Tareas */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-[#D1D3D4]">
        <h4 className="text-lg font-semibold text-[#0061A8] mb-4">Tareas</h4>
        {formData.tasks.map((task, index) => (
          <div key={task.id || index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#63666A]">Descripción</label>
              <input
                type="text"
                name="description"
                value={task.description}
                onChange={(e) => handleTaskChange(index, e)}
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                placeholder="Ej. Enviar propuesta"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#63666A]">Asignado a</label>
              <select
                name="assignedTo"
                value={task.assignedTo}
                onChange={(e) => handleTaskChange(index, e)}
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              >
                <option value="">Seleccionar usuario</option>
                {users.map(user => (
                  <option key={user.email} value={user.firstName + ' ' + user.lastName}>
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#63666A]">Fecha Límite</label>
              <input
                type="date"
                name="dueDate"
                value={task.dueDate}
                onChange={(e) => handleTaskChange(index, e)}
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
            </div>
            {formData.tasks.length > 1 && (
              <button
                type="button"
                onClick={() => removeTaskField(index)}
                className="md:col-span-1 bg-red-500 text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-red-600 transition-colors text-sm"
              >
                Eliminar
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addTaskField}
          className="mt-4 bg-[#A8D08D] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-sm"
        >
          Agregar Tarea
        </button>
      </div>

      {/* Sección de Servicios Vendidos */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-[#D1D3D4]">
        <h4 className="text-lg font-semibold text-[#0061A8] mb-4">Servicios Vendidos</h4>
        {formData.servicesSold.map((service, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3 items-end">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#63666A]">Nombre del Servicio</label>
              <input
                type="text"
                name="name"
                value={service.name}
                onChange={(e) => handleServiceChange(index, e)}
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                placeholder="Ej. Consulta inicial"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#63666A]">Cantidad</label>
              <input
                type="number"
                name="quantity"
                value={service.quantity}
                onChange={(e) => handleServiceChange(index, e)}
                min="0"
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#63666A]">Valor Unitario</label>
              <input
                type="number"
                name="value"
                value={service.value}
                onChange={(e) => handleServiceChange(index, e)}
                min="0"
                step="0.01"
                className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              />
            </div>
            {formData.servicesSold.length > 1 && (
              <button
                type="button"
                onClick={() => removeServiceField(index)}
                className="md:col-span-1 bg-red-500 text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-red-600 transition-colors text-sm"
              >
                Eliminar
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addServiceField}
          className="mt-4 bg-[#A8D08D] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-sm"
        >
          Agregar Servicio
        </button>
      </div>

      {/* Sección de Objeciones */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-[#D1D3D4]">
        <h4 className="text-lg font-semibold text-[#0061A8] mb-4">Objeciones (Pacientes que no agendan)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Por Precio</label>
            <input
              type="number"
              name="price"
              value={formData.objections.price}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Por Ubicación</label>
            <input
              type="number"
              name="location"
              value={formData.objections.location}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">No contestan 1er mensaje</label>
            <input
              type="number"
              name="noResponse"
              value={formData.objections.noResponse}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Por Horarios</label>
            <input
              type="number"
              name="schedule"
              value={formData.objections.schedule}
              onChange={handleObjectionChange}
              min="0"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[#63666A]">Otras Objeciones (describir)</label>
            <textarea
              name="other"
              value={formData.objections.other}
              onChange={handleObjectionChange}
              rows="2"
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            />
          </div>
        </div>
      </div>

      {/* Calculadora de Presupuesto integrada */}
      <BudgetCalculator clientCountry={client.country || 'México'} />

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
        >
          {initialMeetingData ? 'Guardar Cambios' : 'Generar Minuta y Agendar'}
        </button>
      </div>
    </form>
  );
};

export default MeetingForm;
